import React, { useState } from "react";
import { Modal } from "./Modal";
import { trackLeadConversion } from "../../services/analytics";

interface LeadFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  packageName?: string;
}

// Helper function to calculate response time based on submission day/time
function getResponseTimeMessage(): string {
  const now = new Date();
  const day = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const hour = now.getHours();

  // Monday (1) - Thursday (4): 24 hours
  if (day >= 1 && day <= 4) {
    return "We'll contact you within 24 hours.";
  }

  // Friday (5)
  if (day === 5) {
    // Before 12 PM: 24 hours
    if (hour < 12) {
      return "We'll contact you within 24 hours.";
    }
    // At or after 12 PM: 72 hours (covers weekend)
    return "We'll contact you within 72 hours.";
  }

  // Saturday (6)
  if (day === 6) {
    // Before 12 PM: 72 hours
    if (hour < 12) {
      return "We'll contact you within 72 hours.";
    }
    // At or after 12 PM: 48 hours
    return "We'll contact you within 48 hours.";
  }

  // Sunday (0)
  // Before 12 PM: 48 hours
  if (hour < 12) {
    return "We'll contact you within 48 hours.";
  }
  // At or after 12 PM: 24 hours
  return "We'll contact you within 24 hours.";
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  courseInterest: string;
  message: string;
}

export function LeadFormModal({
  isOpen,
  onClose,
  packageName,
}: LeadFormModalProps) {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    courseInterest: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(
        import.meta.env.VITE_CONTACT_WORKER_URL || "http://localhost:8787",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...formData,
            package: packageName,
          }),
        },
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setIsSuccess(true);

        // Track lead conversion in Facebook Pixel and GA4
        trackLeadConversion(packageName);

        setFormData({
          name: "",
          email: "",
          phone: "",
          courseInterest: "",
          message: "",
        });

        // Auto-close modal after 3 seconds
        setTimeout(() => {
          onClose();
          setIsSuccess(false);
        }, 3000);
      } else {
        setError(result.error || "Something went wrong. Please try again.");
      }
    } catch (err) {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: "", email: "", phone: "", courseInterest: "", message: "" });
    setError(null);
    setIsSuccess(false);
    onClose();
  };

  if (isSuccess) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="">
        <div style={{ textAlign: "center", padding: "4rem 2rem" }}>
          <div style={{ fontSize: "4rem", marginBottom: "1rem" }}>✅</div>
          <h2
            style={{
              fontSize: "2.5rem",
              color: "#28a745",
              marginBottom: "1rem",
            }}
          >
            Thank You!
          </h2>
          <p style={{ fontSize: "1.2rem", color: "#666", lineHeight: "1.6" }}>
            {getResponseTimeMessage()}
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Get a Quote Or Inquire">
      <form onSubmit={handleSubmit} style={{ padding: "1rem 0" }}>
        {error && (
          <div
            style={{
              background: "#f8d7da",
              color: "#721c24",
              padding: "1rem",
              borderRadius: "8px",
              marginBottom: "1.5rem",
              border: "1px solid #f5c6cb",
            }}
          >
            {error}
          </div>
        )}

        {/* Name Field */}
        <div style={{ marginBottom: "1.5rem" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "0.5rem",
              flexWrap: "wrap",
              gap: "0.5rem",
            }}
          >
            <label
              htmlFor="name"
              style={{
                fontSize: "1rem",
                fontWeight: 600,
                color: "#333",
              }}
            >
              Name & surname
            </label>
            {packageName && (
              <span
                style={{
                  fontSize: "0.56rem",
                  color: "#000",
                  fontWeight: 600,
                  background: "#ffeb3b",
                  padding: "0.25rem 0.5rem",
                  borderRadius: "4px",
                }}
              >
                Inquiring about: {packageName}
              </span>
            )}
          </div>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Your full name & surname"
            required
            style={{
              width: "100%",
              padding: "1rem",
              border: "2px solid #e9ecef",
              borderRadius: "8px",
              fontSize: "1rem",
              transition: "all 0.3s ease",
              boxSizing: "border-box",
              color: "#333",
              backgroundColor: "#fff",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#8b5cf6";
              e.target.style.boxShadow = "0 0 0 3px rgba(57, 88, 57, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e9ecef";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>

        {/* Email Field */}
        <div style={{ marginBottom: "1.5rem" }}>
          <label
            htmlFor="email"
            style={{
              display: "block",
              fontSize: "1rem",
              fontWeight: 600,
              color: "#333",
              marginBottom: "0.5rem",
            }}
          >
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your.email@example.com"
            required
            style={{
              width: "100%",
              padding: "1rem",
              border: "2px solid #e9ecef",
              borderRadius: "8px",
              fontSize: "1rem",
              transition: "all 0.3s ease",
              boxSizing: "border-box",
              color: "#333",
              backgroundColor: "#fff",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#8b5cf6";
              e.target.style.boxShadow = "0 0 0 3px rgba(57, 88, 57, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e9ecef";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>

        {/* Phone Field */}
        <div style={{ marginBottom: "1.5rem" }}>
          <label
            htmlFor="phone"
            style={{
              display: "block",
              fontSize: "1rem",
              fontWeight: 600,
              color: "#333",
              marginBottom: "0.5rem",
            }}
          >
            Your Phone
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Your phone number"
            required
            style={{
              width: "100%",
              padding: "1rem",
              border: "2px solid #e9ecef",
              borderRadius: "8px",
              fontSize: "1rem",
              transition: "all 0.3s ease",
              boxSizing: "border-box",
              color: "#333",
              backgroundColor: "#fff",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#8b5cf6";
              e.target.style.boxShadow = "0 0 0 3px rgba(57, 88, 57, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e9ecef";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>

        {/* Course Interest Field */}
        <div style={{ marginBottom: "1.5rem" }}>
          <label
            htmlFor="courseInterest"
            style={{
              display: "block",
              fontSize: "1rem",
              fontWeight: 600,
              color: "#333",
              marginBottom: "0.5rem",
            }}
          >
            I am interested in
          </label>
          <select
            id="courseInterest"
            name="courseInterest"
            value={formData.courseInterest}
            onChange={handleChange}
            required
            style={{
              width: "100%",
              padding: "1rem",
              border: "2px solid #e9ecef",
              borderRadius: "8px",
              fontSize: "1rem",
              transition: "all 0.3s ease",
              boxSizing: "border-box",
              color: "#333",
              backgroundColor: "#fff",
              cursor: "pointer",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#8b5cf6";
              e.target.style.boxShadow = "0 0 0 3px rgba(139, 92, 246, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e9ecef";
              e.target.style.boxShadow = "none";
            }}
          >
            <option value="">Select your interest...</option>
            <option value="IGCSE Courses">IGCSE Courses</option>
            <option value="AS Level Courses">AS Level Courses</option>
            <option value="Both IGCSE & AS Level">Both IGCSE & AS Level</option>
            <option value="Specific Subject">Specific Subject (please specify in message)</option>
          </select>
        </div>

        {/* Message Field */}
        <div style={{ marginBottom: "1.5rem" }}>
          <label
            htmlFor="message"
            style={{
              display: "block",
              fontSize: "1rem",
              fontWeight: 600,
              color: "#333",
              marginBottom: "0.5rem",
            }}
          >
            Your Message
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us about your learning goals and what you'd like to achieve..."
            rows={6}
            required
            style={{
              width: "100%",
              padding: "1rem",
              border: "2px solid #e9ecef",
              borderRadius: "8px",
              fontSize: "1rem",
              transition: "all 0.3s ease",
              boxSizing: "border-box",
              resize: "vertical",
              minHeight: "120px",
              fontFamily: "inherit",
              color: "#333",
              backgroundColor: "#fff",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#8b5cf6";
              e.target.style.boxShadow = "0 0 0 3px rgba(57, 88, 57, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e9ecef";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>

        {/* Buttons */}
        <div style={{ display: "flex", gap: "1rem", marginTop: "2rem" }}>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            style={{
              flex: 1,
              padding: "1rem",
              background: "#f8f9fa",
              color: "#666",
              border: "2px solid #e9ecef",
              borderRadius: "50px",
              fontSize: "1.1rem",
              fontWeight: 600,
              cursor: isSubmitting ? "not-allowed" : "pointer",
              opacity: isSubmitting ? 0.7 : 1,
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              flex: 1,
              padding: "1rem",
              background: isSubmitting ? "#999" : "#8b5cf6",
              color: "white",
              border: "none",
              borderRadius: "50px",
              fontSize: "1.1rem",
              fontWeight: 600,
              cursor: isSubmitting ? "not-allowed" : "pointer",
              boxShadow: isSubmitting
                ? "none"
                : "0 4px 15px rgba(57, 88, 57, 0.3)",
              transition: "all 0.3s ease",
              opacity: isSubmitting ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 6px 20px rgba(57, 88, 57, 0.4)";
                e.currentTarget.style.background = "#7c3aed";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 4px 15px rgba(57, 88, 57, 0.3)";
              e.currentTarget.style.background = "#8b5cf6";
            }}
          >
            {isSubmitting ? "Sending..." : "Submit"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
